<template>
  <div>
    <!-- Заголовок -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">{{ $t('settings.profile') }}</h1>
    </div>

    <!-- Profile header section -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <!-- Profile photo -->
          <div class="relative">
            <div v-if="doctorInfo && doctorInfo.user && doctorInfo.user.face_photo" 
                 class="w-20 h-20 rounded-full overflow-hidden">
              <img :src="doctorInfo.user.face_photo" 
                   alt="Фото профиля" 
                   class="w-full h-full object-cover"
                   @error="onImageError">
            </div>
            <div v-else class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
              <svg class="w-10 h-10 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
              </svg>
            </div>
            <!-- Edit photo overlay (only in edit mode) -->
            <div v-if="isEditingProfile" class="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center cursor-pointer" @click="$refs.photoInput.click()">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <!-- Hidden file input -->
            <input 
              ref="photoInput" 
              type="file" 
              accept="image/*" 
              @change="onPhotoSelect" 
              style="display: none"
            >
          </div>
          
          <div v-if="doctorInfo" class="flex-1">
            <h1 class="text-2xl font-bold text-gray-900">{{ formatDoctorName() }}</h1>
            <p class="text-gray-600">{{ formatDoctorSpecialization(doctorInfo) || $t('doctor.doctors') }}</p>
            <div v-if="doctorInfo.city || doctorInfo.clinic" class="mt-1">
              <p class="text-gray-500 text-sm">
                <svg class="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                {{ formatLocation() }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Edit profile button -->
        <div>
          <button v-if="!isEditingProfile" 
                  @click="startEditingProfile" 
                  class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            {{ $t('common.edit') }}
          </button>
          <div v-else class="flex space-x-2">
            <button @click="saveProfile" 
                    class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    :disabled="loading">
              {{ $t('common.save') }}
            </button>
            <button @click="cancelEditingProfile" 
                    class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors">
              {{ $t('common.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile info -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 class="text-xl font-semibold mb-4 text-gray-800">{{ $t('doctor.personalInfo') }}</h2>
      <div v-if="doctorInfo" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('common.firstName') }}</label>
          <input v-if="isEditingProfile" 
                 v-model="editForm.first_name" 
                 type="text" 
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.first_name}"
                 @input="clearProfileError('first_name')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.user?.first_name || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.first_name" class="mt-1 text-sm text-red-600">{{ profileErrors.first_name }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('common.lastName') }}</label>
          <input v-if="isEditingProfile" 
                 v-model="editForm.last_name" 
                 type="text" 
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.last_name}"
                 @input="clearProfileError('last_name')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.user?.last_name || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.last_name" class="mt-1 text-sm text-red-600">{{ profileErrors.last_name }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('common.middleName') }}</label>
          <input v-if="isEditingProfile" 
                 v-model="editForm.middle_name" 
                 type="text" 
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.middle_name}"
                 @input="clearProfileError('middle_name')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.user?.middle_name || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.middle_name" class="mt-1 text-sm text-red-600">{{ profileErrors.middle_name }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('common.email') }}</label>
          <input v-if="isEditingProfile" 
                 v-model="editForm.email" 
                 type="email" 
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.email}"
                 @input="clearProfileError('email')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.user?.email }}</p>
          <p v-if="profileErrors.email" class="mt-1 text-sm text-red-600">{{ profileErrors.email }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('common.phone') }}</label>
          <input v-if="isEditingProfile" 
                 v-model="editForm.phone" 
                 type="tel" 
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.phone}"
                 @input="clearProfileError('phone')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.user?.phone || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.phone" class="mt-1 text-sm text-red-600">{{ profileErrors.phone }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('common.iin') }}</label>
          <p class="mt-1 text-sm text-gray-900">{{ doctorInfo.user?.iin }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('common.dateOfBirth') }}</label>
          <input v-if="isEditingProfile" 
                 v-model="editForm.date_of_birth" 
                 type="date" 
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.date_of_birth}"
                 @input="clearProfileError('date_of_birth')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ formatDate(doctorInfo.user?.date_of_birth) }}</p>
          <p v-if="profileErrors.date_of_birth" class="mt-1 text-sm text-red-600">{{ profileErrors.date_of_birth }}</p>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700">{{ $t('doctor.experienceYears') }}</label>
          <input v-if="isEditingProfile" 
                 v-model="editForm.experience_years" 
                 type="number" 
                 min="0" 
                 max="50"
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.experience_years}"
                 @input="clearProfileError('experience_years')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.experience_years || $t('common.notSpecified') }} {{ doctorInfo.experience_years ? $t('time.years') : '' }}</p>
          <p v-if="profileErrors.experience_years" class="mt-1 text-sm text-red-600">{{ profileErrors.experience_years }}</p>
        </div>
        
        <div v-if="userType === 'doctor'">
          <label class="block text-sm font-medium text-gray-700">{{ $t('doctor.specialization') }}</label>
          <select v-if="isEditingProfile" 
                  v-model="editForm.specialization_ref" 
                  class="form-input mt-1"
                  :class="{'border-red-500': profileErrors.specialization_ref}"
                  @change="clearProfileError('specialization_ref')">
            <option value="">{{ $t('common.notSpecified') }}</option>
            <option v-for="spec in specializations" :key="spec.id" :value="spec.id">
              {{ spec.display_name || spec.name }}
            </option>
          </select>
          <p v-else class="mt-1 text-sm text-gray-900">{{ formatDoctorSpecialization(doctorInfo) || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.specialization_ref" class="mt-1 text-sm text-red-600">{{ profileErrors.specialization_ref }}</p>
        </div>

        <div v-if="userType === 'doctor'">
          <label class="block text-sm font-medium text-gray-700">{{ $t('doctor.academicDegree') }}</label>
          <input v-if="isEditingProfile"
                 v-model="editForm.academic_degree"
                 type="text"
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.academic_degree}"
                 @input="clearProfileError('academic_degree')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.academic_degree || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.academic_degree" class="mt-1 text-sm text-red-600">{{ profileErrors.academic_degree }}</p>
        </div>

        <div v-if="userType === 'doctor'">
          <label class="block text-sm font-medium text-gray-700">{{ $t('doctor.academicTitle') }}</label>
          <input v-if="isEditingProfile"
                 v-model="editForm.academic_title"
                 type="text"
                 class="form-input mt-1"
                 :class="{'border-red-500': profileErrors.academic_title}"
                 @input="clearProfileError('academic_title')">
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.academic_title || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.academic_title" class="mt-1 text-sm text-red-600">{{ profileErrors.academic_title }}</p>
        </div>
        
        <div v-if="userType === 'nurse'">
          <label class="block text-sm font-medium text-gray-700">{{ $t('doctor.specialization') }}</label>
          <select v-if="isEditingProfile" 
                  v-model="editForm.specialization" 
                  class="form-input mt-1"
                  :class="{'border-red-500': profileErrors.specialization}"
                  @change="clearProfileError('specialization')">
            <option value="">{{ $t('common.notSpecified') }}</option>
            <option v-for="spec in specializations" :key="spec.id" :value="spec.display_name || spec.name">
              {{ spec.display_name || spec.name }}
            </option>
          </select>
          <p v-else class="mt-1 text-sm text-gray-900">{{ doctorInfo.specialization || $t('common.notSpecified') }}</p>
          <p v-if="profileErrors.specialization" class="mt-1 text-sm text-red-600">{{ profileErrors.specialization }}</p>
        </div>
        
        <!-- Поля для изменения пароля (только в режиме редактирования) -->
        <div v-if="isEditingProfile" class="col-span-full">
          <div class="border-t border-gray-200 pt-6 mt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">{{ $t('settings.changePassword') }}</h3>
            <p class="text-sm text-gray-600 mb-4">{{ $t('settings.changePasswordDescription') }}</p>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">{{ $t('settings.currentPassword') }}</label>
                <input v-model="passwordForm.old_password" 
                       type="password" 
                       class="form-input mt-1"
                       :class="{'border-red-500': profileErrors.old_password}"
                       @input="clearProfileError('old_password')"
                       placeholder="Оставьте пустым, если не хотите менять пароль">
                <p v-if="profileErrors.old_password" class="mt-1 text-sm text-red-600">{{ profileErrors.old_password }}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700">{{ $t('settings.newPassword') }}</label>
                <input v-model="passwordForm.new_password" 
                       type="password" 
                       class="form-input mt-1"
                       :class="{'border-red-500': profileErrors.new_password || (passwordValidationErrors.length > 0 && passwordForm.new_password)}"
                       @input="validateNewPassword"
                       :placeholder="$t('passwordValidation.minCharacters')">
                
                <!-- Interactive password requirements -->
                <div v-if="passwordForm.new_password || passwordForm.old_password" class="mt-2 text-xs text-gray-600">
                  <p class="font-medium mb-1">{{ $t('passwordValidation.requirements') }}:</p>
                  <ul class="space-y-1">
                    <li v-for="(req, idx) in passwordRequirements" :key="idx" class="flex items-center">
                      <svg class="w-3 h-3 mr-1" :class="isPasswordRequirementMet(idx) ? 'text-green-500' : 'text-gray-400'" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                      <span :class="isPasswordRequirementMet(idx) ? 'text-green-600' : 'text-gray-600'">{{ req }}</span>
                    </li>
                  </ul>
                </div>
                
                <p v-if="profileErrors.new_password" class="mt-1 text-sm text-red-600">{{ profileErrors.new_password }}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700">{{ $t('settings.confirmNewPassword') }}</label>
                <input v-model="passwordForm.new_password_confirm" 
                       type="password" 
                       class="form-input mt-1"
                       :class="{'border-red-500': profileErrors.new_password_confirm}"
                       @input="clearProfileError('new_password_confirm')"
                       placeholder="Повторите новый пароль">
                <p v-if="profileErrors.new_password_confirm" class="mt-1 text-sm text-red-600">{{ profileErrors.new_password_confirm }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Error and success messages for profile -->
      <div v-if="profileSubmitError" class="bg-red-50 p-4 rounded-md mt-4 text-red-700">
        {{ profileSubmitError }}
      </div>
      
      <div v-if="profileSuccessMessage" class="bg-green-50 p-4 rounded-md mt-4 text-green-700">
        {{ profileSuccessMessage }}
      </div>
    </div>

    <!-- Visit reasons and prices for commercial clinics -->
    <div v-if="isCommercialClinicDoctor" class="bg-white rounded-xl shadow-sm border border-amber-200 p-6 mb-6">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 class="text-xl font-semibold text-amber-800">Причины приема и цены</h2>
          <p class="mt-1 text-sm text-amber-700">
            Управляйте доступными причинами приема и стоимостью по каждой причине.
          </p>
        </div>
        <button
          @click="openVisitReasonsConfigModal"
          class="inline-flex items-center justify-center rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
        >
          Управление причинами и ценами
        </button>
      </div>
    </div>

    <!-- Login History Section -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-800">{{ $t('loginHistory.title') }}</h2>
            <p class="text-sm text-gray-500">{{ $t('loginHistory.subtitle') }}</p>
          </div>
        </div>
        <button 
          @click="fetchLoginHistory" 
          class="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200"
          :class="{'animate-spin': loginHistoryLoading}"
          :disabled="loginHistoryLoading"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
        </button>
      </div>

      <!-- Loading state -->
      <div v-if="loginHistoryLoading" class="flex justify-center p-8">
        <HeartbeatLoader :text="$t('loginHistory.loading')" />
      </div>

      <!-- Error state -->
      <div v-else-if="loginHistoryError" class="text-center p-6 bg-red-50 rounded-lg">
        <svg class="w-12 h-12 mx-auto mb-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="text-red-600">{{ loginHistoryError }}</p>
      </div>

      <!-- Login history list -->
      <div v-else-if="loginHistory.length > 0" class="space-y-3">
        <div 
          v-for="attempt in displayedLoginHistory" 
          :key="attempt.id"
          class="flex items-center justify-between p-4 rounded-lg border transition-all duration-200 hover:shadow-md"
          :class="{
            'bg-green-50 border-green-200 hover:border-green-300': attempt.status === 'success',
            'bg-red-50 border-red-200 hover:border-red-300': attempt.status === 'failed',
            'bg-orange-50 border-orange-200 hover:border-orange-300': attempt.status === 'blocked'
          }"
        >
          <!-- Left: Status icon and info -->
          <div class="flex items-center space-x-4">
            <div 
              class="w-10 h-10 rounded-full flex items-center justify-center"
              :class="{
                'bg-green-100': attempt.status === 'success',
                'bg-red-100': attempt.status === 'failed',
                'bg-orange-100': attempt.status === 'blocked'
              }"
            >
              <!-- Success icon -->
              <svg v-if="attempt.status === 'success'" class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <!-- Failed icon -->
              <svg v-else-if="attempt.status === 'failed'" class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
              <!-- Blocked icon -->
              <svg v-else class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
              </svg>
            </div>
            <div>
              <p class="font-medium" :class="{
                'text-green-800': attempt.status === 'success',
                'text-red-800': attempt.status === 'failed',
                'text-orange-800': attempt.status === 'blocked'
              }">
                {{ attempt.status_display }}
              </p>
              <p class="text-sm text-gray-500">{{ attempt.created_at_formatted }}</p>
            </div>
          </div>

          <!-- Right: IP and device info -->
          <div class="text-right">
            <div class="flex items-center justify-end space-x-2 mb-1">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
              </svg>
              <span class="text-sm font-mono text-gray-600">{{ attempt.ip_address }}</span>
            </div>
            <p v-if="attempt.user_agent" class="text-xs text-gray-400 max-w-[200px] truncate">
              {{ getDeviceInfo(attempt.user_agent) }}
            </p>
          </div>
        </div>

        <!-- Show more button -->
        <div v-if="loginHistory.length > 5 && !showAllLoginHistory" class="text-center pt-4">
          <button 
            @click="showAllLoginHistory = true"
            class="px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200 text-sm font-medium"
          >
            {{ $t('loginHistory.showAll') }} ({{ loginHistory.length }})
          </button>
        </div>
        
        <!-- Collapse button -->
        <div v-if="showAllLoginHistory && loginHistory.length > 5" class="text-center pt-4">
          <button 
            @click="showAllLoginHistory = false"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200 text-sm font-medium"
          >
            {{ $t('loginHistory.collapse') }}
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="text-center p-8 bg-gray-50 rounded-lg">
        <svg class="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
        </svg>
        <p class="text-gray-500">{{ $t('loginHistory.noData') }}</p>
      </div>
    </div>

    <!-- Visit Reasons & Prices Config Modal (Commercial clinics) -->
    <div v-if="showVisitReasonsConfigModal" class="fixed inset-0 z-[75] overflow-y-auto">
      <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeVisitReasonsConfigModal"></div>

      <div class="flex min-h-screen items-center justify-center p-4">
        <div class="relative z-10 w-full max-w-5xl rounded-xl border border-gray-200 bg-white shadow-xl">
          <div class="flex items-start justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Причины приема и цены</h2>
              <p class="mt-1 text-sm text-gray-600">
                Отметьте причины, доступные пациенту, и укажите стоимость по каждой причине.
              </p>
            </div>
            <button
              @click="closeVisitReasonsConfigModal"
              class="rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <div v-if="visitReasonsConfigLoading" class="flex items-center justify-center py-10">
              <HeartbeatLoader :text="$t('common.loading')" />
            </div>

            <div v-else>
              <div v-if="visitReasonsConfigError" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {{ visitReasonsConfigError }}
              </div>

              <div class="overflow-x-auto rounded-lg border border-gray-200">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Доступно</th>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Причина приема</th>
                      <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Цена (₸)</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 bg-white">
                    <tr v-for="row in visitReasonsConfigRows" :key="row.id">
                      <td class="px-4 py-3 align-top">
                        <input
                          v-model="row.selected"
                          @change="handleVisitReasonSelection(row)"
                          type="checkbox"
                          class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td class="px-4 py-3 align-top">
                        <p class="text-sm font-medium text-gray-900">{{ getLocalizedVisitReasonName(row) }}</p>
                        <p v-if="row.description" class="mt-1 text-xs text-gray-500">{{ row.description }}</p>
                      </td>
                      <td class="px-4 py-3 align-top">
                        <input
                          v-model="row.price"
                          @blur="onVisitReasonPriceBlur(row)"
                          :disabled="!row.selected"
                          type="number"
                          min="0"
                          step="0.01"
                          class="w-40 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                          placeholder="0.00"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p class="mt-3 text-xs text-gray-500">
                Неотмеченные причины автоматически становятся недоступными для записи.
              </p>
            </div>
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              @click="closeVisitReasonsConfigModal"
              class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              @click="saveVisitReasonsConfig"
              :disabled="visitReasonsConfigLoading || visitReasonsConfigSaving"
              class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              {{ visitReasonsConfigSaving ? 'Сохранение...' : 'Сохранить' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Schedule management section -->

    <div v-if="userType === 'doctor'" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 class="text-xl font-semibold mb-6 text-gray-800">{{ $t('doctor.scheduleManagement') }}</h2>
      
      <div v-if="loading" class="flex justify-center p-6">
        <HeartbeatLoader :text="$t('common.loading')" />
      </div>
      
      <div v-else>
        <p class="mb-6 text-gray-700">
          {{ $t('schedule.scheduleDescription') }}
        </p>
        
        <div v-if="schedules.length > 0" class="mb-8">
          <h3 class="text-lg font-medium mb-4 text-gray-800">{{ $t('schedule.currentSchedules') }}</h3>
          
          <div v-for="schedule in schedules" :key="schedule.id" class="border rounded-lg p-4 mb-4 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-center">
              <div>
                <h4 class="font-medium text-gray-900">{{ getDayName(schedule.day_of_week) }}</h4>
                <p class="text-gray-600">
                  {{ formatTime(schedule.start_time) }} - {{ formatTime(schedule.end_time) }}
                </p>
                <p v-if="schedule.lunch_break_start && schedule.lunch_break_end" class="text-gray-500 text-sm">
                  {{ $t('doctor.lunchBreak') }}: {{ formatTime(schedule.lunch_break_start) }} - {{ formatTime(schedule.lunch_break_end) }}
                </p>
                <p v-else class="text-gray-500 text-sm">
                  {{ $t('schedule.noLunchBreak') }}
                </p>
              </div>
              <div class="flex space-x-2">
                <button 
                  @click="editSchedule(schedule)" 
                  class="px-3 py-1 text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                >
                  {{ $t('common.edit') }}
                </button>
                <button 
                  @click="deleteSchedule(schedule.id)" 
                  class="px-3 py-1 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                >
                  {{ $t('common.delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="text-center p-6 bg-blue-50 rounded-lg mb-8">
          <p class="text-gray-700">{{ $t('schedule.noSchedulesYet') }}</p>
          <p class="text-gray-600 mt-2">{{ $t('schedule.addScheduleToStart') }}</p>
        </div>
        
        <div id="schedule-form">
          <h3 class="text-lg font-medium mb-4 text-gray-800">
            {{ isEditing ? $t('schedule.editSchedule') : $t('schedule.addSchedule') }}
          </h3>
          
          <form @submit.prevent="saveSchedule" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('form.selectDayOfWeek') }}</label>
              <select 
                v-model="currentSchedule.day_of_week"
                class="form-input"
                :class="{'border-red-500': errors.day_of_week}"
                @change="clearError('day_of_week')"
              >
                <option value="" disabled>{{ $t('form.selectDayOfWeek') }}</option>
                <option 
                  v-for="(day, index) in daysOfWeek" 
                  :key="index" 
                  :value="index"
                  :disabled="isDayOccupied(index)"
                  :class="{ 'text-gray-400': isDayOccupied(index) }"
                >
                  {{ day }}
                  <span v-if="isDayOccupied(index)"> - {{ $t('schedule.dayAlreadyScheduledHint') }}</span>
                </option>
              </select>
              <p v-if="errors.day_of_week" class="mt-1 text-sm text-red-600">{{ errors.day_of_week }}</p>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('doctor.startTime') }}</label>
                <input 
                  type="time" 
                  v-model="currentSchedule.start_time"
                  class="form-input"
                  :class="{'border-red-500': errors.start_time}"
                  @input="clearError('start_time')"
                >
                <p v-if="errors.start_time" class="mt-1 text-sm text-red-600">{{ errors.start_time }}</p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('doctor.endTime') }}</label>
                <input 
                  type="time" 
                  v-model="currentSchedule.end_time"
                  class="form-input"
                  :class="{'border-red-500': errors.end_time}"
                  @input="clearError('end_time')"
                >
                <p v-if="errors.end_time" class="mt-1 text-sm text-red-600">{{ errors.end_time }}</p>
              </div>
            </div>
            
            <div class="border-t pt-4 mt-4">
              <div class="flex items-center justify-between mb-2">
                <h4 class="font-medium text-gray-800">{{ $t('schedule.lunchBreakOptional') }}</h4>
                <button 
                  v-if="currentSchedule.lunch_break_start || currentSchedule.lunch_break_end"
                  type="button"
                  @click="removeLunchBreak"
                  class="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                >
                  {{ $t('schedule.removeLunchBreak') }}
                </button>
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('schedule.lunchStart') }}</label>
                  <input 
                    type="time" 
                    v-model="currentSchedule.lunch_break_start"
                    class="form-input"
                    :class="{'border-red-500': errors.lunch_break_start}"
                    @input="clearError('lunch_break_start')"
                    :placeholder="$t('schedule.lunchStartPlaceholder')"
                  >
                  <p v-if="errors.lunch_break_start" class="mt-1 text-sm text-red-600">{{ errors.lunch_break_start }}</p>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('schedule.lunchEnd') }}</label>
                  <input 
                    type="time" 
                    v-model="currentSchedule.lunch_break_end"
                    class="form-input"
                    :class="{'border-red-500': errors.lunch_break_end}"
                    @input="clearError('lunch_break_end')"
                    :placeholder="$t('schedule.lunchEndPlaceholder')"
                  >
                  <p v-if="errors.lunch_break_end" class="mt-1 text-sm text-red-600">{{ errors.lunch_break_end }}</p>
                </div>
              </div>
              
              <p class="text-sm text-gray-500 mt-2">
                {{ $t('schedule.lunchBreakHint') }}
              </p>
            </div>
            
            <div class="flex justify-between pt-4">
              <button 
                type="button"
                @click="resetForm"
                class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {{ $t('common.cancel') }}
              </button>
              
              <button 
                type="submit"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                :disabled="loading"
              >
                {{ isEditing ? $t('messages.saveChanges') : $t('schedule.addSchedule') }}
              </button>
            </div>
          </form>
        </div>
        
        <div v-if="submitError" class="bg-red-50 p-4 rounded-md mt-6 text-red-700">
          {{ submitError }}
        </div>
        
        <div v-if="successMessage" class="bg-green-50 p-4 rounded-md mt-6 text-green-700">
          {{ successMessage }}
        </div>
      </div>
    </div>

    <!-- MIS Nadezhda Schedule Section -->
    <div v-if="userType === 'doctor' && hasMISIntegration" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
      <div class="flex justify-between items-center mb-6">
        <div>
          <h2 class="text-xl font-semibold text-gray-800">График в МИС Надежда</h2>
          <p class="text-sm text-gray-500 mt-1">Ваше расписание во внешней медицинской информационной системе</p>
        </div>
        <button 
          v-if="!showMISSchedule"
          @click="fetchMISSchedule" 
          class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center"
          :disabled="misScheduleLoading"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          Посмотреть график
        </button>
        <div v-else class="flex space-x-2">
          <button 
            @click="fetchMISSchedule" 
            class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            :disabled="misScheduleLoading"
          >
            {{ misScheduleLoading ? 'Загрузка...' : 'Обновить' }}
          </button>
          <button 
            @click="hideMISSchedule" 
            class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Скрыть
          </button>
        </div>
      </div>
      
      <div v-if="showMISSchedule">
        <div v-if="misScheduleLoading" class="flex justify-center p-6">
          <HeartbeatLoader text="Загрузка графика из МИС..." />
        </div>
        
        <div v-else-if="misScheduleError" class="text-center p-6 bg-yellow-50 rounded-lg">
          <svg class="w-12 h-12 mx-auto mb-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <p class="text-gray-700">{{ misScheduleError }}</p>
        </div>
        
        <div v-else-if="misSchedule && misSchedule.length > 0">
          <!-- Информация о специальности -->
          <div v-if="misScheduleName || misSpecialityName" class="mb-4 p-3 bg-purple-100 rounded-lg">
            <p v-if="misScheduleName" class="font-medium text-purple-800">{{ misScheduleName }}</p>
            <p v-if="misSpecialityName" class="text-sm text-purple-600">{{ misSpecialityName }}</p>
          </div>
          
          <!-- Предпросмотр изменений -->
          <div v-if="schedulePreview" class="mb-4">
            
            <!-- Если все идентично -->
            <div v-if="schedulePreview.identicalDays.length > 0 && schedulePreview.newDays.length === 0 && schedulePreview.conflictingDays.length === 0" class="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
              <p class="text-green-800 font-medium">✓ График полностью синхронизирован</p>
              <p class="text-green-600 text-sm mt-1">Все дни в локальном расписании соответствуют графику МИС Надежда</p>
            </div>

            <!-- Если есть изменения -->
            <div v-else class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 class="font-medium text-blue-900 mb-3">Предпросмотр изменений:</h4>
            
              <div v-if="schedulePreview.newDays.length > 0" class="mb-3">
                <p class="text-sm font-medium text-green-700 mb-1">✓ Будут добавлены ({{ schedulePreview.newDays.length }}):</p>
                <div class="flex flex-wrap gap-2">
                  <span v-for="day in schedulePreview.newDays" :key="day" class="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                    {{ getDayName(day) }}
                  </span>
                </div>
              </div>
              
              <div v-if="schedulePreview.conflictingDays.length > 0" class="mb-3">
                <p class="text-sm font-medium text-orange-700 mb-1">⚠ Отличаются ({{ schedulePreview.conflictingDays.length }}):</p>
                <div class="space-y-2 mt-2">
                  <div v-for="conflict in schedulePreview.conflictingDays" :key="conflict.dayIndex" class="text-xs bg-white p-2 rounded border border-orange-200">
                    <span class="font-bold text-orange-800 block mb-1">{{ getDayName(conflict.dayIndex) }}</span>
                    <div class="grid grid-cols-2 gap-2 text-gray-600">
                      <div>
                        <span class="text-gray-400 text-[10px] uppercase block">МИС Надежда</span>
                        <span>{{ conflict.misParams.start_time.substring(0,5) }} - {{ conflict.misParams.end_time.substring(0,5) }}</span>
                      </div>
                      <div>
                        <span class="text-gray-400 text-[10px] uppercase block">Текущее</span>
                        <span>{{ conflict.localParams.start_time.substring(0,5) }} - {{ conflict.localParams.end_time.substring(0,5) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="schedulePreview.identicalDays.length > 0" class="mt-2 text-xs text-gray-500">
                + {{ schedulePreview.identicalDays.length }} дн. без изменений
              </div>
              
              <div v-if="schedulePreview.totalMisWorkdays === 0" class="text-sm text-gray-600">
                В графике МИС нет рабочих дней
              </div>
            </div>
          </div>
          
          <!-- Чекбокс замены существующих -->
          <div v-if="schedulePreview && schedulePreview.conflictingDays.length > 0" class="mb-4">
            <label class="flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                v-model="replaceExisting" 
                class="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              >
              <span class="ml-2 text-sm text-gray-700">
                Обновить отличающиеся расписания ({{ schedulePreview.conflictingDays.length }} дн.)
              </span>
            </label>
            <p class="ml-6 text-xs text-gray-500 mt-1">
              При включении старые расписания будут обновлены данными из МИС
            </p>
          </div>
          
          <!-- Кнопка установки графика из МИС -->
          <div class="mb-6" v-if="schedulePreview && (schedulePreview.newDays.length > 0 || schedulePreview.conflictingDays.length > 0)">
            <button 
              @click="setMISSchedule" 
              class="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center w-full justify-center"
              :disabled="setMISScheduleLoading || !canInstallSchedule"
            >
              <svg v-if="!setMISScheduleLoading" class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
              </svg>
              <svg v-else class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ setMISScheduleLoading ? 'Установка...' : 'Установить график из МИС Надежды' }}
            </button>
            
            <div v-if="setMISScheduleErrors && setMISScheduleErrors.length > 0" class="mt-3 p-3 bg-red-50 rounded-md text-red-700 text-sm">
              <p class="font-medium mb-1">Ошибки при создании расписаний:</p>
              <ul class="list-disc list-inside">
                <li v-for="(error, idx) in setMISScheduleErrors" :key="idx">{{ error }}</li>
              </ul>
            </div>
            
            <div v-if="setMISScheduleSuccess" class="mt-3 p-3 bg-green-50 rounded-md text-green-700 text-sm">
              {{ setMISScheduleSuccess }}
            </div>
          </div>
          
          <!-- График по дням недели -->
          <div v-for="(scheduleItem, idx) in misSchedule" :key="idx" class="mb-4">
            <div v-if="scheduleItem.timetable" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              <div 
                v-for="(dayData, dayName) in scheduleItem.timetable" 
                :key="dayName"
                class="border rounded-lg p-4 hover:shadow-md transition-shadow"
                :class="dayData.is_workday ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'"
              >
                <h4 class="font-medium mb-2" :class="dayData.is_workday ? 'text-purple-800' : 'text-gray-500'">
                  {{ getMISDayName(dayName) }}
                </h4>
                <div v-if="dayData.is_workday" class="space-y-1">
                  <div class="text-sm text-gray-700 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    {{ dayData.work_start_tm }} - {{ dayData.work_end_tm }}
                  </div>
                  <div v-if="dayData.break_start_tm && dayData.break_end_tm" class="text-xs text-gray-500">
                    Перерыв: {{ dayData.break_start_tm }} - {{ dayData.break_end_tm }}
                  </div>
                </div>
                <div v-else class="text-sm text-gray-400">
                  Выходной
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="text-center p-6 bg-gray-50 rounded-lg">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <p class="text-gray-600">График не найден в МИС Надежда</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import staffApi from '@/services/staffApi'
import HeartbeatLoader from '@/components/common/HeartbeatLoader.vue'
import { validatePasswordComplexity, getPasswordRequirements } from '@/utils/passwordValidator'
import { formatDoctorSpecialization as formatDoctorSpecializationText } from '@/utils/doctorDisplay'

export default {
  name: 'ProfilePage',
  components: {
    HeartbeatLoader
  },
  watch: {
    schedules: {
      handler() {
        if (this.showMISSchedule) {
          this.generateSchedulePreview()
        }
      },
      deep: true
    }
  },
  data() {
    return {
      doctorInfo: null,
      loading: false,
      schedules: [],
      isEditing: false,
      currentSchedule: this.getEmptySchedule(),
      errors: {},
      submitError: '',
      successMessage: '',
      userType: null,
      
      // Profile editing
      isEditingProfile: false,
      editForm: {},
      originalData: {},
      profileErrors: {},
      profileSubmitError: '',
      profileSuccessMessage: '',
      photoFile: null,
      photoToDelete: false,
      passwordForm: {
        old_password: '',
        new_password: '',
        new_password_confirm: ''
      },
      
      // MIS Schedule
      hasMISIntegration: false,
      misSchedule: null,
      misScheduleName: null,
      misSpecialityName: null,
      misScheduleLoading: false,
      misScheduleError: null,
      showMISSchedule: false,
      
      // MIS Schedule Import
      setMISScheduleLoading: false,
      setMISScheduleErrors: [],
      setMISScheduleSuccess: null,
      replaceExisting: false,
      schedulePreview: null,
      
      // Login History
      loginHistory: [],
      loginHistoryLoading: false,
      loginHistoryError: null,
      showAllLoginHistory: false,
      
      // Password Validation
      passwordValidationErrors: [],
      passwordRequirements: getPasswordRequirements(),
      
      // Specializations
      specializations: [],
      loadingSpecializations: false,

      // Commercial clinic visit reasons config
      showVisitReasonsConfigModal: false,
      visitReasonsConfigLoading: false,
      visitReasonsConfigSaving: false,
      visitReasonsConfigError: null,
      visitReasonsConfigRows: [],
      allVisitReasonsForConfig: []
    }
  },
  computed: {
    isCommercialClinicDoctor() {
      return this.userType === 'doctor' && this.doctorInfo?.clinic?.clinic_type === 'commercial'
    },
    daysOfWeek() {
      return [
        this.$t('schedule.monday'),
        this.$t('schedule.tuesday'),
        this.$t('schedule.wednesday'),
        this.$t('schedule.thursday'),
        this.$t('schedule.friday'),
        this.$t('schedule.saturday'),
        this.$t('schedule.sunday')
      ]
    },
    occupiedDays() {
      // Возвращает массив дней недели, для которых уже есть графики
      return this.schedules.map(schedule => schedule.day_of_week)
    },
    isDayOccupied() {
      // Функция для проверки, занят ли день (исключая редактируемый график)
      return (dayIndex) => {
        if (this.isEditing && this.currentSchedule.id) {
          // При редактировании исключаем текущий редактируемый график
          return this.schedules
            .filter(schedule => schedule.id !== this.currentSchedule.id)
            .some(schedule => schedule.day_of_week === dayIndex)
        }
        return this.occupiedDays.includes(dayIndex)
      }
    },
    canInstallSchedule() {
      // Можно установить, если:
      // 1. Есть предпросмотр
      // 2. Есть изменения (новые или конфликтующие дни)
      // 3. Если есть конфликты - чекбокс update должен быть включен (иначе кнопка будет активна только для новых)
      
      if (!this.schedulePreview) return false
      
      const hasNew = this.schedulePreview.newDays.length > 0
      const hasConflicts = this.schedulePreview.conflictingDays.length > 0
      
      if (!hasNew && !hasConflicts) return false // Все идентично
      
      // Если есть конфликты и нет новых дней -> нужен чекбокс
      if (hasConflicts && !hasNew && !this.replaceExisting) return false
      
      // Если есть и новые и конфликты -> без чекбокса установим только новые, с чекбоксом - всё. 
      // Кнопку можно оставить активной, но setMISSchedule сам решит что делать.
      // Но лучше блокировать, если юзер хочет обновить конфликты, но не нажал галочку? 
      // Нет, позволим нажать, просто конфликты не обновятся (skipping).
      
      return true
    },
    displayedLoginHistory() {
      // Показываем только 5 записей, если не нажата кнопка "Показать все"
      return this.showAllLoginHistory 
        ? this.loginHistory 
        : this.loginHistory.slice(0, 5)
    }
  },
  async created() {
    await this.checkUserTypeAndFetchInfo()
  },
  methods: {
    async checkUserTypeAndFetchInfo() {
      try {
        // Сначала проверяем тип пользователя
        const authResponse = await api.checkAuth()
        this.userType = authResponse.data.user?.type
        
        // Загружаем профиль в зависимости от типа пользователя
        await this.fetchDoctorInfo()
        
        // Загружаем историю входов для всех пользователей
        await this.fetchLoginHistory()
        
        // Загружаем расписание только для врачей
        if (this.userType === 'doctor') {
          await this.fetchSchedules()
          // Проверяем наличие интеграции МИС
          await this.checkMISIntegration()
        }
      } catch (error) {
      // Error handled silently
      }
    },
    async fetchSpecializations() {
      this.loadingSpecializations = true
      try {
        const response = await api.get('/specializations/list_for_select/')
        this.specializations = response.data
      } catch (error) {
        console.error('Failed to fetch specializations:', error)
        // Fallback or empty list
        this.specializations = []
      } finally {
        this.loadingSpecializations = false
      }
    },
    async fetchDoctorInfo() {
      try {
        let response
        if (this.userType === 'doctor') {
          response = await api.getDoctorProfile()
        } else if (this.userType === 'nurse') {
          response = await api.getNurseProfile()
        }
        this.doctorInfo = response.data
      } catch (error) {
      // Error handled silently
      }
    },
    formatDoctorName() {
      if (!this.doctorInfo || !this.doctorInfo.user) return ''
      
      const user = this.doctorInfo.user
      const parts = []
      
      if (user.last_name) parts.push(user.last_name)
      if (user.first_name) parts.push(user.first_name)
      if (user.middle_name) parts.push(user.middle_name)
      
      return parts.join(' ') || (this.userType === 'nurse' ? this.$t('nurse.nurse') : this.$t('doctor.doctors'))
    },
    formatDoctorSpecialization(doctor) {
      if (this.userType !== 'doctor') {
        return doctor?.specialization || ''
      }

      return formatDoctorSpecializationText(doctor)
    },
    formatLocation() {
      if (!this.doctorInfo) return ''
      const parts = []
      if (this.doctorInfo.city?.name) parts.push(this.doctorInfo.city.name)
      if (this.doctorInfo.clinic) parts.push(this.getLocalizedClinicName(this.doctorInfo.clinic))

      return parts.join(', ')
    },
    getLocalizedClinicName(clinic) {
      if (this.$i18n.locale === 'kk' && clinic.name_kz) {
        return clinic.name_kz;
      }
      // Возвращаем русское название или обычное name как fallback
      return clinic.name || '';
    },
    formatDate(dateString) {
      if (!dateString) return this.$t('common.notSpecified')
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU')
    },
    getEmptySchedule() {
      return {
        day_of_week: '',
        start_time: '',
        end_time: '',
        lunch_break_start: null,
        lunch_break_end: null,
        is_active: true
      }
    },
    clearError(field) {
      if (this.errors[field]) {
        this.errors[field] = null
      }
    },
    formatTime(timeString) {
      if (!timeString) return ''
      
      const timeParts = timeString.split(':')
      const hours = parseInt(timeParts[0])
      const minutes = timeParts[1]
      
      return `${hours}:${minutes}`
    },
    getDayName(dayIndex) {
      const days = [
        this.$t('schedule.monday'),
        this.$t('schedule.tuesday'), 
        this.$t('schedule.wednesday'),
        this.$t('schedule.thursday'),
        this.$t('schedule.friday'),
        this.$t('schedule.saturday'),
        this.$t('schedule.sunday')
      ]
      return days[dayIndex] || ''
    },
    async fetchLoginHistory() {
      this.loginHistoryLoading = true
      this.loginHistoryError = null
      
      try {
        const response = await api.getLoginHistory()
        this.loginHistory = response.data.attempts || []
      } catch (error) {
        console.error('Failed to fetch login history:', error)
        this.loginHistoryError = this.$t('loginHistory.loadError')
      } finally {
        this.loginHistoryLoading = false
      }
    },
    getDeviceInfo(userAgent) {
      if (!userAgent) return ''
      
      // Простой парсинг User-Agent для отображения понятной информации
      let device = ''
      let browser = ''
      
      // Определяем устройство
      if (userAgent.includes('iPhone')) {
        device = 'iPhone'
      } else if (userAgent.includes('iPad')) {
        device = 'iPad'
      } else if (userAgent.includes('Android')) {
        device = 'Android'
      } else if (userAgent.includes('Windows')) {
        device = 'Windows'
      } else if (userAgent.includes('Mac')) {
        device = 'macOS'
      } else if (userAgent.includes('Linux')) {
        device = 'Linux'
      }
      
      // Определяем браузер
      if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        browser = 'Chrome'
      } else if (userAgent.includes('Firefox')) {
        browser = 'Firefox'
      } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        browser = 'Safari'
      } else if (userAgent.includes('Edg')) {
        browser = 'Edge'
      }
      
      if (device && browser) {
        return `${browser} ${this.$t('loginHistory.on')} ${device}`
      } else if (device) {
        return device
      } else if (browser) {
        return browser
      }
      
      return userAgent.substring(0, 30) + '...'
    },
    validateNewPassword() {
      const validation = validatePasswordComplexity(this.passwordForm.new_password)
      this.passwordValidationErrors = validation.errors
      // Also clear the regular validation error when user types
      this.clearProfileError('new_password')
    },
    isPasswordRequirementMet(index) {
      const password = this.passwordForm.new_password
      if (!password) return false
      
      switch(index) {
        case 0: return password.length >= 8
        case 1: return /\d/.test(password)
        case 2: return /[A-ZА-ЯЁ]/.test(password)
        case 3: return /[a-zа-яё]/.test(password)
        case 4: return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)
        default: return false
      }
    },
    buildVisitReasonsConfigRows(allReasons = [], doctorConfigs = []) {
      const configByReasonId = new Map()
      doctorConfigs.forEach((cfg) => {
        configByReasonId.set(cfg.visit_reason, cfg)
      })

      return allReasons.map((reason) => {
        const cfg = configByReasonId.get(reason.id)
        const selected = cfg ? Boolean(cfg.is_active) : false
        const price = cfg && cfg.price !== null && cfg.price !== undefined
          ? String(cfg.price)
          : '0.00'

        return {
          id: reason.id,
          name: reason.name || reason.name_ru || '',
          name_kk: reason.name_kk || '',
          description: reason.description || '',
          selected,
          price
        }
      })
    },
    getLocalizedVisitReasonName(reason) {
      const locale = this.$i18n.locale
      if (locale === 'kk' && reason.name_kk) {
        return reason.name_kk
      }
      return reason.name || reason.name_ru || 'Неизвестная причина'
    },
    async openVisitReasonsConfigModal() {
      if (!this.isCommercialClinicDoctor) return

      this.showVisitReasonsConfigModal = true
      this.visitReasonsConfigLoading = true
      this.visitReasonsConfigSaving = false
      this.visitReasonsConfigError = null

      try {
        const [allReasonsResponse, doctorConfigsResponse] = await Promise.all([
          api.getVisitReasons(),
          staffApi.getDoctorVisitReasonsConfig()
        ])

        const allReasons = Array.isArray(allReasonsResponse.data) ? allReasonsResponse.data : []
        const doctorConfigs = Array.isArray(doctorConfigsResponse.data) ? doctorConfigsResponse.data : []

        this.allVisitReasonsForConfig = allReasons
        this.visitReasonsConfigRows = this.buildVisitReasonsConfigRows(allReasons, doctorConfigs)
      } catch (error) {
        this.visitReasonsConfigError = 'Не удалось загрузить настройки причин и цен. Попробуйте еще раз.'
      } finally {
        this.visitReasonsConfigLoading = false
      }
    },
    closeVisitReasonsConfigModal() {
      if (this.visitReasonsConfigSaving) return

      this.showVisitReasonsConfigModal = false
      this.visitReasonsConfigError = null
    },
    handleVisitReasonSelection(row) {
      if (!row) return

      if (row.selected && (!row.price || Number(row.price) < 0)) {
        row.price = '0.00'
      }
    },
    isValidVisitReasonPrice(rawPrice) {
      if (rawPrice === '' || rawPrice === null || rawPrice === undefined) return false

      const price = Number(rawPrice)
      return Number.isFinite(price) && price >= 0
    },
    normalizeVisitReasonPrice(rawPrice) {
      return Number(rawPrice).toFixed(2)
    },
    onVisitReasonPriceBlur(row) {
      if (!row || !row.selected) return

      if (this.isValidVisitReasonPrice(row.price)) {
        row.price = this.normalizeVisitReasonPrice(row.price)
      }
    },
    async saveVisitReasonsConfig() {
      const selectedRows = this.visitReasonsConfigRows.filter((row) => row.selected)

      const invalidRow = selectedRows.find((row) => !this.isValidVisitReasonPrice(row.price))
      if (invalidRow) {
        this.visitReasonsConfigError = `Укажите корректную цену для причины: ${this.getLocalizedVisitReasonName(invalidRow)}`
        return
      }

      const payload = {
        configs: selectedRows.map((row) => ({
          visit_reason_id: row.id,
          price: this.normalizeVisitReasonPrice(row.price),
          is_active: true
        }))
      }

      this.visitReasonsConfigSaving = true
      this.visitReasonsConfigError = null

      try {
        const response = await staffApi.updateDoctorVisitReasonsConfig(payload)
        const updatedConfigs = Array.isArray(response.data) ? response.data : []
        this.visitReasonsConfigRows = this.buildVisitReasonsConfigRows(
          this.allVisitReasonsForConfig,
          updatedConfigs
        )
        this.profileSuccessMessage = 'Настройки причин приема и цен успешно сохранены'
        this.showVisitReasonsConfigModal = false
      } catch (error) {
        const serverDetail = error.response?.data?.detail
        this.visitReasonsConfigError = serverDetail || 'Не удалось сохранить настройки причин и цен.'
      } finally {
        this.visitReasonsConfigSaving = false
      }
    },
    async fetchSchedules() {
      this.loading = true
      
      try {
        const response = await api.getDoctorSchedules()
        
        if (response.data && response.data.results) {
          this.schedules = response.data.results
        } else if (response.data && Array.isArray(response.data)) {
          this.schedules = response.data
        } else {
          this.schedules = []
        }
        
        this.schedules.forEach(schedule => {
        })
      } catch (error) {
        this.submitError = 'Не удалось загрузить графики работы'
      } finally {
        this.loading = false
      }
    },
    async saveSchedule() {
      if (!this.validateSchedule()) {
        return
      }
      
      this.loading = true
      
      try {
        let response;
        if (this.isEditing && this.currentSchedule.id) {
          response = await api.updateDoctorSchedule(this.currentSchedule.id, this.currentSchedule)
          this.successMessage = 'График успешно обновлен'
          
          await this.fetchSchedules()
        } else {
          response = await api.createDoctorSchedule(this.currentSchedule)
          this.successMessage = 'График успешно добавлен'
          
          await this.fetchSchedules()
        }
        
        this.resetForm()
        
        setTimeout(() => {
          this.successMessage = ''
        }, 5000)
      } catch (error) {
        
        if (error.response && error.response.data) {
          this.submitError = this.formatErrorMessage(error.response.data)
        } else {
          this.submitError = 'Не удалось сохранить график работы'
        }
      } finally {
        this.loading = false
      }
    },
    validateSchedule() {
      this.errors = {}
      let isValid = true
      
      if (!this.currentSchedule.day_of_week && this.currentSchedule.day_of_week !== 0) {
        this.errors.day_of_week = 'Выберите день недели'
        isValid = false
      }
      
      if (!this.currentSchedule.start_time) {
        this.errors.start_time = 'Укажите время начала'
        isValid = false
      }
      
      if (!this.currentSchedule.end_time) {
        this.errors.end_time = 'Укажите время окончания'
        isValid = false
      }
      
      // Проверяем обеденный перерыв только если хотя бы одно поле заполнено
      const hasLunchStart = !!this.currentSchedule.lunch_break_start
      const hasLunchEnd = !!this.currentSchedule.lunch_break_end
      
      if (hasLunchStart && !hasLunchEnd) {
        this.errors.lunch_break_end = this.$t('form.lunchEndRequired') || 'Укажите время окончания обеда'
        isValid = false
      }
      
      if (!hasLunchStart && hasLunchEnd) {
        this.errors.lunch_break_start = this.$t('form.lunchStartRequired') || 'Укажите время начала обеда'
        isValid = false
      }
      
      return isValid
    },
    resetForm() {
      this.currentSchedule = this.getEmptySchedule()
      this.isEditing = false
      this.errors = {}
      this.submitError = ''
    },
    editSchedule(schedule) {
      this.currentSchedule = { ...schedule }
      this.isEditing = true
      
      window.scrollTo({
        top: document.getElementById('schedule-form').offsetTop,
        behavior: 'smooth'
      })
    },
    async deleteSchedule(scheduleId) {
      if (!confirm('Вы уверены, что хотите удалить этот график?')) {
        return
      }
      
      this.loading = true
      
      try {
        await api.deleteDoctorSchedule(scheduleId)
        this.successMessage = 'График успешно удален'
        
        await this.fetchSchedules()
        
        setTimeout(() => {
          this.successMessage = ''
        }, 3000)
      } catch (error) {
        this.submitError = 'Не удалось удалить график работы'
      } finally {
        this.loading = false
      }
    },
    formatErrorMessage(errorData) {
      if (typeof errorData === 'string') {
        return errorData
      }
      
      if (typeof errorData === 'object') {
        const messages = []
        
        for (const key in errorData) {
          const value = errorData[key]
          
          if (Array.isArray(value)) {
            messages.push(`${key}: ${value.join(', ')}`)
          } else {
            messages.push(`${key}: ${value}`)
          }
        }
        
        return messages.join('\n')
      }
      
      return 'Неизвестная ошибка'
    },
    
    // Profile editing methods
    startEditingProfile() {
      this.isEditingProfile = true
      this.profileErrors = {}
      this.profileSubmitError = ''
      this.profileSuccessMessage = ''
      this.photoFile = null
      this.photoToDelete = false
      
      // Очищаем форму пароля
      this.passwordForm = {
        old_password: '',
        new_password: '',
        new_password_confirm: ''
      }
      
      // Создаем копию данных для редактирования
      this.originalData = JSON.parse(JSON.stringify(this.doctorInfo))
      this.editForm = {
        first_name: this.doctorInfo.user?.first_name || '',
        last_name: this.doctorInfo.user?.last_name || '',
        middle_name: this.doctorInfo.user?.middle_name || '',
        email: this.doctorInfo.user?.email || '',
        phone: this.doctorInfo.user?.phone || '',
        date_of_birth: this.doctorInfo.user?.date_of_birth || '',
        specialization: this.doctorInfo.specialization || '',
        specialization_ref: this.doctorInfo.specialization_ref_id || null,
        experience_years: this.doctorInfo.experience_years || 0,
        academic_degree: this.userType === 'doctor' ? (this.doctorInfo.academic_degree || '') : undefined,
        academic_title: this.userType === 'doctor' ? (this.doctorInfo.academic_title || '') : undefined
      }
      
      // Загружаем список специальностей
      this.fetchSpecializations()
    },
    
    cancelEditingProfile() {
      this.isEditingProfile = false
      this.editForm = {}
      this.profileErrors = {}
      this.profileSubmitError = ''
      this.profileSuccessMessage = ''
      this.photoFile = null
      this.photoToDelete = false
      
      // Очищаем форму пароля
      this.passwordForm = {
        old_password: '',
        new_password: '',
        new_password_confirm: ''
      }
    },
    
    clearProfileError(field) {
      if (this.profileErrors[field]) {
        this.profileErrors[field] = null
      }
    },
    
    onPhotoSelect(event) {
      const file = event.target.files[0]
      
      if (file) {
        // Проверяем размер файла (максимум 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.profileSubmitError = 'Размер файла не должен превышать 5MB'
          return
        }

        // Проверяем тип файла
        if (!file.type.startsWith('image/')) {
          this.profileSubmitError = 'Можно загружать только изображения'
          return
        }

        this.photoFile = file
        this.photoToDelete = false
        this.profileSubmitError = ''
        
      }
    },
    
    onImageError(event) {
      event.target.style.display = 'none'
    },
    
    validateProfile() {
      this.profileErrors = {}
      let isValid = true

      if (!this.editForm.first_name?.trim()) {
        this.profileErrors.first_name = 'Имя обязательно'
        isValid = false
      }

      if (!this.editForm.last_name?.trim()) {
        this.profileErrors.last_name = 'Фамилия обязательна'
        isValid = false
      }

      if (!this.editForm.email?.trim()) {
        this.profileErrors.email = 'Email обязателен'
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.editForm.email)) {
        this.profileErrors.email = 'Неверный формат email'
        isValid = false
      }

      if (this.editForm.phone?.trim() && !/^\+?[0-9\s\-()]+$/.test(this.editForm.phone)) {
        this.profileErrors.phone = 'Неверный формат телефона'
        isValid = false
      }

      // Валидация пароля (если поля пароля заполнены)
      const hasPasswordFields = this.passwordForm.old_password || this.passwordForm.new_password || this.passwordForm.new_password_confirm

      if (hasPasswordFields) {
        if (!this.passwordForm.old_password) {
          this.profileErrors.old_password = 'Введите текущий пароль'
          isValid = false
        }

        if (!this.passwordForm.new_password) {
          this.profileErrors.new_password = 'Введите новый пароль'
          isValid = false
        } else {
          // Используем полную валидацию сложности пароля
          const passwordValidation = validatePasswordComplexity(this.passwordForm.new_password)
          if (!passwordValidation.isValid) {
            this.profileErrors.new_password = passwordValidation.errors[0]
            isValid = false
          }
        }

        if (!this.passwordForm.new_password_confirm) {
          this.profileErrors.new_password_confirm = 'Подтвердите новый пароль'
          isValid = false
        } else if (this.passwordForm.new_password !== this.passwordForm.new_password_confirm) {
          this.profileErrors.new_password_confirm = 'Пароли не совпадают'
          isValid = false
        }
      }

      return isValid
    },
    
    async changePassword() {
      try {
        await api.changePassword(this.passwordForm)
        return true
      } catch (error) {
        
        if (error.response?.data) {
          // Обрабатываем ошибки валидации от сервера
          const errorData = error.response.data
          
          if (errorData.old_password) {
            this.profileErrors.old_password = Array.isArray(errorData.old_password) ? errorData.old_password[0] : errorData.old_password
          }
          if (errorData.new_password) {
            this.profileErrors.new_password = Array.isArray(errorData.new_password) ? errorData.new_password[0] : errorData.new_password
          }
          if (errorData.new_password_confirm) {
            this.profileErrors.new_password_confirm = Array.isArray(errorData.new_password_confirm) ? errorData.new_password_confirm[0] : errorData.new_password_confirm
          }
        }
        
        throw error
      }
    },
    
    async saveProfile() {
      if (!this.validateProfile()) {
        return
      }

      this.loading = true
      this.profileSubmitError = ''
      this.profileSuccessMessage = ''
      

      try {
        // Сначала обновляем основные данные профиля
        const formData = new FormData()
        
        // Добавляем базовые данные
        Object.keys(this.editForm).forEach(key => {
          if (this.editForm[key] !== null && this.editForm[key] !== undefined) {
            formData.append(key, this.editForm[key])
          }
        })

        // Добавляем фото если есть
        if (this.photoFile) {
          formData.append('face_photo', this.photoFile)
        }

        // Отправляем флаг удаления фото
        if (this.photoToDelete) {
          formData.append('delete_photo', 'true')
        }

        for (let [key, value] of formData.entries()) {
          if (key === 'face_photo') {
          }
        }

        let response
        if (this.userType === 'doctor') {
          response = await api.updateDoctorProfile(formData)
        } else if (this.userType === 'nurse') {
          response = await api.updateNurseProfile(formData)
        }


        // Обновляем локальные данные
        this.doctorInfo = response.data
        
        // Если есть данные для изменения пароля, меняем пароль
        const hasPasswordFields = this.passwordForm.old_password || this.passwordForm.new_password || this.passwordForm.new_password_confirm
        if (hasPasswordFields) {
          try {
            await this.changePassword()
            this.profileSuccessMessage = 'Профиль и пароль успешно обновлены'
          } catch (passwordError) {
            this.profileSuccessMessage = 'Профиль обновлен, но при изменении пароля произошла ошибка'
            return // Выходим, чтобы не закрывать форму редактирования
          }
        } else {
          this.profileSuccessMessage = 'Профиль успешно обновлен'
        }
        
        this.isEditingProfile = false
        this.photoFile = null
        this.photoToDelete = false
        
        // Очищаем форму пароля
        this.passwordForm = {
          old_password: '',
          new_password: '',
          new_password_confirm: ''
        }

        // Убираем сообщение через 5 секунд
        setTimeout(() => {
          this.profileSuccessMessage = ''
        }, 5000)

      } catch (error) {
        this.profileSubmitError = error.response?.data?.detail || 
                                  error.response?.data?.message || 
                                  'Не удалось обновить профиль'
      } finally {
        this.loading = false
      }
    },
    removeLunchBreak() {
      this.currentSchedule.lunch_break_start = null;
      this.currentSchedule.lunch_break_end = null;
      this.clearError('lunch_break_start');
      this.clearError('lunch_break_end');
    },
    
    // MIS Schedule methods
    async checkMISIntegration() {
      try {
        const response = await api.getDoctorMISSchedule()
        this.hasMISIntegration = response.data.has_integration
        if (response.data.error) {
          this.misScheduleError = response.data.error
        }
      } catch (error) {
        this.hasMISIntegration = false
      }
    },
    
    async fetchMISSchedule() {
      this.misScheduleLoading = true
      this.misScheduleError = null
      this.showMISSchedule = true
      
      try {
        const response = await api.getDoctorMISSchedule()
        console.log('MIS Schedule response:', response.data)
        this.hasMISIntegration = response.data.has_integration
        this.misSchedule = response.data.schedule
        this.misScheduleName = response.data.schedule_name
        this.misSpecialityName = response.data.speciality_name
        if (response.data.error) {
          this.misScheduleError = response.data.error
        }
        
        // Генерируем предпросмотр после загрузки
        this.generateSchedulePreview()
      } catch (error) {
        console.error('MIS Schedule error:', error)
        this.misScheduleError = 'Не удалось загрузить график из МИС'
      } finally {
        this.misScheduleLoading = false
      }
    },
    
    getMISDayName(dayName) {
      const dayNames = {
        'monday': 'Понедельник',
        'tuesday': 'Вторник',
        'wednesday': 'Среда',
        'thursday': 'Четверг',
        'friday': 'Пятница',
        'saturday': 'Суббота',
        'sunday': 'Воскресенье'
      }
      return dayNames[dayName] || dayName
    },
    
    hideMISSchedule() {
      this.showMISSchedule = false
      this.schedulePreview = null
      this.replaceExisting = false
    },
    
    generateSchedulePreview() {
      // Генерируем предпросмотр изменений
      if (!this.misSchedule || this.misSchedule.length === 0) {
        this.schedulePreview = null
        return
      }
      
      const timetable = this.misSchedule[0].timetable
      if (!timetable) {
        this.schedulePreview = null
        return
      }
      
      const dayMapping = {
        'monday': 0,
        'tuesday': 1,
        'wednesday': 2,
        'thursday': 3,
        'friday': 4,
        'saturday': 5,
        'sunday': 6
      }
      
      const newDays = []
      const conflictingDays = []
      const identicalDays = []
      let totalMisWorkdays = 0
      
      for (const [dayName, dayData] of Object.entries(timetable)) {
        if (dayData.is_workday) {
          totalMisWorkdays++
          const dayIndex = dayMapping[dayName]
          const existingSchedule = this.schedules.find(s => s.day_of_week === dayIndex)
          
          if (existingSchedule) {
            // Если расписание есть, проверяем отличается ли оно
            const misParams = {
              start_time: dayData.work_start_tm,
              end_time: dayData.work_end_tm,
              lunch_break_start: dayData.break_start_tm || null,
              lunch_break_end: dayData.break_end_tm || null
            }
            
            if (!this.schedulesAreEqual(existingSchedule, misParams)) {
              conflictingDays.push({
                dayIndex,
                misParams,
                localParams: existingSchedule
              })
            } else {
              identicalDays.push(dayIndex)
            }
          } else {
            newDays.push(dayIndex)
          }
        }
      }
      
      this.schedulePreview = {
        newDays: newDays.sort((a, b) => a - b),
        conflictingDays: conflictingDays.sort((a, b) => a.dayIndex - b.dayIndex),
        identicalDays: identicalDays.sort((a, b) => a - b),
        totalMisWorkdays
      }
    },

    // Сравнение времени (игнорируем секунды если они есть)
    compareTimes(t1, t2) {
      if (!t1 && !t2) return true
      if (!t1 || !t2) return false
      // Берем только HH:MM
      return t1.substring(0, 5) === t2.substring(0, 5)
    },

    // Сравнение расписаний
    schedulesAreEqual(local, mis) {
      return this.compareTimes(local.start_time, mis.start_time) &&
             this.compareTimes(local.end_time, mis.end_time) &&
             this.compareTimes(local.lunch_break_start, mis.lunch_break_start) &&
             this.compareTimes(local.lunch_break_end, mis.lunch_break_end)
    },
    
    async setMISSchedule() {
      this.setMISScheduleLoading = true
      this.setMISScheduleErrors = []
      this.setMISScheduleSuccess = null
      
      try {
        // 1. Сначала обновляем локальный список расписаний, чтобы иметь актуальные данные
        await this.fetchSchedules()
        
        // Проверяем, что график из МИС загружен
        if (!this.misSchedule || this.misSchedule.length === 0) {
          this.setMISScheduleErrors = ['График из МИС не загружен']
          return
        }
        
        const timetable = this.misSchedule[0].timetable
        if (!timetable) {
          this.setMISScheduleErrors = ['Расписание не найдено в данных МИС']
          return
        }
        
        // Маппинг дней недели
        const dayMapping = {
          'monday': 0,
          'tuesday': 1,
          'wednesday': 2,
          'thursday': 3,
          'friday': 4,
          'saturday': 5,
          'sunday': 6
        }
        
        // Create or Update schedules
        let successCount = 0
        let updatedCount = 0
        let skippedCount = 0
        let errorMessages = []
        
        for (const [dayName, dayData] of Object.entries(timetable)) {
          if (dayData.is_workday) {
            const dayIndex = dayMapping[dayName]
            const dayNameRus = this.getDayName(dayIndex)
            
            const scheduleData = {
              day_of_week: dayIndex,
              start_time: dayData.work_start_tm,
              end_time: dayData.work_end_tm,
              is_active: true,
              lunch_break_start: dayData.break_start_tm || null,
              lunch_break_end: dayData.break_end_tm || null
            }

            const existingSchedule = this.schedules.find(s => s.day_of_week === dayIndex)

            try {
              if (existingSchedule) {
                // Если расписание существует
                if (this.schedulesAreEqual(existingSchedule, scheduleData)) {
                  // Идентично - пропускаем
                  skippedCount++
                  continue
                }

                if (this.replaceExisting) {
                  // Отличается и можно заменять - обновляем (PUT)
                  await api.updateDoctorSchedule(existingSchedule.id, scheduleData)
                  updatedCount++
                } else {
                  // Отличается и нельзя заменять - ошибка конфликта
                  // Но мы не останавливаемся, просто пишем ошибку для этого дня
                  // errorMessages.push(`${dayNameRus}: Расписание уже существует и отличается (включите замену)`)
                  // Хотя по логике UI кнопка disabled если есть конфликты и нет галочки.
                  // Но на всякий случай обработаем
                }
              } else {
                // Расписания нет - создаем (POST)
                await api.createDoctorSchedule(scheduleData)
                successCount++
              }
            } catch (error) {
               console.error(`Error processing day ${dayIndex}:`, error)
               const errorMsg = error.response?.data?.detail || error.response?.data?.non_field_errors?.[0] || 'Ошибка сохранения'
               errorMessages.push(`${dayNameRus}: ${errorMsg}`)
            }
          }
        }
        
        // Обновляем список расписаний
        await this.fetchSchedules()
        
        // Обновляем предпросмотр после создания
        this.generateSchedulePreview()
        
        // Показываем результат
        // Показываем результат (учитываем создание, обновление и пропуски)
        const totalProcessed = successCount + updatedCount + skippedCount
        
        if (successCount > 0 || updatedCount > 0) {
          const parts = []
          if (successCount > 0) parts.push(`создано: ${successCount}`)
          if (updatedCount > 0) parts.push(`обновлено: ${updatedCount}`)
          if (skippedCount > 0) parts.push(`без изменений: ${skippedCount}`)
          
          this.setMISScheduleSuccess = `Успешно! ${parts.join(', ')}`
          
          // Скрываем сообщение через 5 секунд
          setTimeout(() => {
            this.setMISScheduleSuccess = null
          }, 5000)
        } else if (skippedCount > 0) {
           this.setMISScheduleSuccess = `Все расписания уже актуальны (${skippedCount} дн.)`
           setTimeout(() => {
             this.setMISScheduleSuccess = null
           }, 3000)
        }
        
        if (errorMessages.length > 0) {
          this.setMISScheduleErrors = errorMessages
        }
        
      } catch (error) {
        console.error('Error setting MIS schedule:', error)
        this.setMISScheduleErrors = ['Не удалось установить график из МИС']
      } finally {
        this.setMISScheduleLoading = false
      }
    }
  }
}
</script>

<style scoped>
.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500;
}
</style> 
